import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { Button } from '~/components/Button';
import { Modal } from '~/components/Modal';
import { RadioButtonGroup } from '~/components/RadioButtonGroup/RadioButtonGroup';

const EXPORT_BASIC = 'roster';
const EXPORT_WITH_CONTACTS = 'rosterAndContact';

interface ArtistExportDialogProps {
  search?: string;
  labels?: number[];
  onExportClick?: (withContactInfo: boolean) => void;
  isOpen: boolean;
  onChange: (state: boolean) => void;
}

const ArtistExportDialog = ({
  onExportClick,
  isOpen,
  onChange,
}: ArtistExportDialogProps) => {
  const { t } = useTranslation('roster');
  const [exportType, setExportType] = useState(EXPORT_BASIC);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onChange(false);
      }}
    >
      <div className="flex flex-col gap-xl rounded-sm p-xl bg-default text-white">
        <div className="flex items-center justify-between align-middle">
          <div className="flex items-center gap-sm align-middle">
            <Icon type="file-export" variant="regular" size="md" />{' '}
            <span className="text-headline-md font-semibold">
              {t('export.labels.header')}
            </span>
          </div>
          <button
            onClick={(event) => {
              event.stopPropagation();
              onChange(false);
            }}
            className="inline-flex size-sm justify-center rounded-sm p-md duration-300 ease-out hover:bg-secondary-hover"
          >
            <Icon
              type="xmark"
              variant="regular"
              size="lg"
              aria-label={t('export.labels.close')}
            />
          </button>
        </div>
        <div>
          <RadioButtonGroup
            items={[
              {
                value: EXPORT_BASIC,
                labelComponent: (
                  <div className="px-sm">{t('export.labels.roster')}</div>
                ),
              },
              {
                value: EXPORT_WITH_CONTACTS,
                labelComponent: (
                  <div className="px-sm">
                    <div className="text-body-sm text-subtle">
                      {t('export.labels.confidential')}
                    </div>
                    <div>{t('export.labels.rosterAndContact')}</div>
                  </div>
                ),
              },
            ]}
            defaultValue={EXPORT_BASIC}
            itemClassName="hover:border-emphasis-stronger duration-300 ease-out"
            itemSelectedClassName="border-emphasis-stronger"
            onSelect={setExportType}
          />
        </div>
        <div className="text-body-sm">
          <div>
            <Trans
              t={t}
              i18nKey="export.labels.subtext"
              components={{ br: <br /> }}
            />
          </div>
        </div>
        <div className="flex justify-end space-x-lg">
          <Button
            className="font-semibold"
            variant="text"
            onClick={(event) => {
              event.stopPropagation();
              onChange(false);
            }}
          >
            {t('export.labels.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={(event) => {
              event.stopPropagation();
              onExportClick?.(exportType === EXPORT_WITH_CONTACTS);
            }}
          >
            {t('export.labels.export')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

ArtistExportDialog.displayName = 'ArtistExportDialog';

export { ArtistExportDialog };
